import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import Loader from "components/Atoms/SharedComponents/Loader/Loader";
import BookingsTemplate from "templates/events/bookings";
import { getBookingsRequest } from "services/modules/events/bookings";
import { getScrollEventTypesRequest } from "services/modules/events/eventTypes";
import { useThemePalette } from "common/hooks/theme_palette";

const BOOKING_STATUSES = [
  "confirmed",
  "in_preparation",
  "completed",
  "cancelled",
];

function BookingsPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orange } = useThemePalette();

  const list = useSelector((state) => state.bookings.bookings);
  const count = useSelector((state) => state.bookings.count);
  const load = useSelector((state) => state.bookings.load);
  const filterEventTypes = useSelector(
    (state) => state.eventTypes.filterEventTypes,
  );

  const [page, setPage] = useState(1);
  const [filterObj, setFilterObj] = useState(null);

  const statusOptions = BOOKING_STATUSES.map((s) => ({
    id: s,
    name: t(`events_bookings.statuses.${s}`, { defaultValue: s }),
  }));

  const eventTypesOptions =
    filterEventTypes?.map((et) => ({
      id: et.id,
      name:
        et?.localized_data?.name_en ||
        et?.localized_data?.en?.name ||
        et?.name_en ||
        et?.name ||
        `#${et.id}`,
    })) || [];

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    setFilterObj((prev) => ({ ...prev, page_number: nextPage }));
  };

  const getList = (filters) => {
    dispatch(
      getBookingsRequest({
        params: {
          page_size: 10,
          page_number: 1,
          keyword: "",
          sort_by: "id",
          ...filters,
        },
      }),
    );
  };

  const mappedData = list?.map((data) => ({
    ...data,
    customer_name:
      data?.customer?.name || data?.contact?.name || data?.customer_name || "-",
    event_type_name:
      data?.event_type?.name_en || data?.event_type?.name || "-",
    package_name: data?.package?.name_en || data?.package?.name || "-",
    event_datetime: `${data?.event_date || ""} ${data?.event_time || ""}`.trim(),
    total_label: `${data?.total ?? "-"} ${data?.currency || "SAR"}`,
    booking_status_label: t(
      `events_bookings.statuses.${data?.booking_status}`,
      { defaultValue: data?.booking_status },
    ),
    payment_status_label: t(
      `events_bookings.payment_statuses.${data?.payment_status}`,
      { defaultValue: data?.payment_status },
    ),
  }));

  const breadcrumbs = [
    { active: true, path: "/", title: t("common.home") },
    {
      active: false,
      path: "/events/bookings",
      title: t("events_bookings.title"),
    },
  ];

  const filterList = [
    {
      type: "filter",
      placeholder: t("events_bookings.booking_status"),
      onChange: (value) => {
        setFilterObj((s) => ({
          ...s,
          booking_status: value?.value,
          page_number: 1,
        }));
      },
      options: statusOptions,
    },
    {
      type: "filter",
      placeholder: t("events_bookings.event_type"),
      onChange: (value) => {
        setFilterObj((s) => ({
          ...s,
          event_type_id: value?.value,
          page_number: 1,
        }));
      },
      options: eventTypesOptions,
    },
    {
      type: "date",
      placeholder: t("events_bookings.event_date_from"),
      onChange: (value) => {
        if (value !== "01-01-1970") {
          setFilterObj((s) => ({
            ...s,
            event_date_from: value
              ? dayjs(value, "DD-MM-YYYY").format("YYYY-MM-DD")
              : "",
            page_number: 1,
          }));
        } else {
          setFilterObj((s) => ({ ...s, event_date_from: "" }));
        }
      },
      sx: {
        backgroundColor: "white",
        ".MuiSvgIcon-root": { color: orange },
      },
    },
    {
      type: "date",
      placeholder: t("events_bookings.event_date_to"),
      onChange: (value) => {
        if (value !== "01-01-1970") {
          setFilterObj((s) => ({
            ...s,
            event_date_to: value
              ? dayjs(value, "DD-MM-YYYY").format("YYYY-MM-DD")
              : "",
            page_number: 1,
          }));
        } else {
          setFilterObj((s) => ({ ...s, event_date_to: "" }));
        }
      },
      sx: {
        backgroundColor: "white",
        ".MuiSvgIcon-root": { color: orange },
      },
    },
  ];

  const actionsList = ({ data }) => [
    {
      condition: () => true,
      click: () => navigate(`/events/bookings/${data?.id}`),
      text: t("common.details"),
    },
  ];

  useEffect(() => {
    dispatch(
      getScrollEventTypesRequest({
        params: { page_size: 100, page_number: 1 },
      }),
    );
  }, []);

  useEffect(() => {
    if (filterObj) getList(filterObj);
  }, [filterObj]);

  return (
    <>
      <Loader open={load?.loading && load?.type === "list"} />
      <BookingsTemplate
        setFilterObj={setFilterObj}
        data={mappedData}
        count={count}
        paginationPage={page}
        handlePageChange={handlePageChange}
        rowData={() => {}}
        breadcrumbs={breadcrumbs}
        filterList={filterList}
        actionsList={actionsList}
      />
    </>
  );
}

export default BookingsPage;
